"use strict";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = __importDefault(require("react"));
const eventemitter3_1 = __importDefault(require("eventemitter3"));
const sha256_1 = require("@zedoc/sha256");
const ResourcesManager_1 = __importDefault(require("./ResourcesManager"));
const actions_1 = require("./actions");
const identity = (x) => x;
const errors = ['loginError', 'resumeLoginError', 'logoutError', 'error'];
class DDPConnector extends eventemitter3_1.default {
    constructor({ debug, ddpClient, cleanupDelay = 60 * 1000, cleanupDelayOnError = 1000, entitiesUpdateDelay = 100, resourceUpdateDelay = 100, getMessageChannel = null, defaultLoaderComponent = () => react_1.default.createElement('div', {}, ['Loading ...']), transformRequest = identity, }) {
        super();
        this.resourceUpdateDelay = resourceUpdateDelay;
        this.entitiesUpdateDelay = entitiesUpdateDelay;
        this.defaultLoaderComponent = defaultLoaderComponent;
        this.ddp = ddpClient;
        // NOTE: This is not a nice solution. Will need to fix it.
        this.ddp.models = this.constructor.models;
        this.endpoint = ddpClient.endpoint;
        this.ddp.on('dataUpdated', (newCollections) => {
            this.scheduleUpdates(newCollections, entitiesUpdateDelay);
        });
        // Trigger flush right after receiving a DDP "updated" message.
        this.ddp.on('updated', () => {
            this.flushUpdates();
        });
        // NOTE: If there are not explicit handlers for these errors,
        //       they will at least be printed into the console.
        errors.forEach((name) => {
            this.ddp.on(name, (...args) => {
                this.emit(name, ...args);
            });
            this.on(name, (...args) => {
                if (this.listenerCount(name) <= 1) {
                    console.error(...args);
                }
            });
        });
        this.subsManager = new ResourcesManager_1.default({
            cleanupDelay,
            cleanupDelayOnError,
            resourcesFactory: (request, requestMeta, cb) => {
                const { name, params } = transformRequest(request, Object.assign({ type: DDPConnector.REQUEST_TYPE__SUBSCRIPTION }, requestMeta));
                let handle = this.ddp.subscribe(name, params, {
                    onReady: () => cb(),
                    // NOTE: This is problematic because onStop can also be called
                    //       when subscription is stopped with "handle.stop()"!
                    //       but there will be no error in such case
                    onStop: (err) => cb && err && cb(err),
                });
                return {
                    stop: () => {
                        if (handle) {
                            handle.stop();
                            handle = null;
                            cb = null; // eslint-disable-line no-param-reassign
                        }
                    },
                };
            },
        });
        this.queryManager = new ResourcesManager_1.default({
            cleanupDelay,
            cleanupDelayOnError,
            resourcesFactory: (request, requestMeta, cb) => {
                const { name, params } = transformRequest(request, Object.assign({ type: DDPConnector.REQUEST_TYPE__QUERY }, requestMeta));
                // NOTE: In this particular case we don't really need to wait until flush,
                //       so we call "apply" on ddp object directly.
                this.ddp.apply(name, params, {}, cb);
                return {
                    stop: () => {
                        cb = null; // eslint-disable-line no-param-reassign
                    },
                };
            },
        });
        this.ddp.on('loggedOut', () => {
            this.subsManager.cleanupResources();
            this.queryManager.cleanupResources();
        });
        if (getMessageChannel) {
            const handleCollectionMessage = ({ collection, id, fields }) => {
                const channel = getMessageChannel(collection);
                if (channel) {
                    this.emit(`messages.${channel}`, id, fields);
                }
            };
            this.ddp.on('added', handleCollectionMessage);
            this.ddp.on('changed', handleCollectionMessage);
        }
        if (debug) {
            this.ddp.socket.on('message:out', (message) => console.warn('DDP/OUT', message));
            this.ddp.socket.on('message:in', (message) => console.warn('DDP/IN', message));
        }
    }
    getLoaderComponent() {
        return this.defaultLoaderComponent;
    }
    // accounts
    static hashPassword(password) {
        return {
            digest: (0, sha256_1.sha256hex)(password),
            algorithm: 'sha-256',
        };
    }
    // models
    static registerModel(Model) {
        if (!Model.collection) {
            throw Error(`No collection name assigned to model ${Model.name}`);
        }
        this.models[Model.collection] = Model;
    }
    // map events to redux actions
    bindToStore(store) {
        const dispatch = store.dispatch.bind(store);
        // NOTE: Since "status.connected" has effect on where exactly the entities
        //       will be stored, we need to flush every time before the status changes.
        this.ddp.on('connected', () => {
            this.flushUpdates();
            dispatch((0, actions_1.setConnected)(true));
        });
        this.ddp.on('disconnected', () => {
            this.flushUpdates();
            dispatch((0, actions_1.setConnected)(false));
        });
        // subscriptions
        this.subsManager.on('create', ({ id, request }) => dispatch((0, actions_1.createSubscription)({ id, request })));
        this.subsManager.on('delete', ({ id }) => dispatch((0, actions_1.deleteSubscription)({ id })));
        this.subsManager.on('ready', ({ id }) => {
            const state = store.getState();
            const restoring = state && state.ddp && state.ddp.status && state.ddp.status.restoring;
            if (restoring) {
                dispatch((0, actions_1.updateSubscription)({ id, pendingReady: true }));
            }
            else {
                this.flushUpdates();
                dispatch((0, actions_1.updateSubscription)({ id, ready: true }));
            }
        });
        this.subsManager.on('error', ({ id, error }) => {
            this.flushUpdates();
            dispatch((0, actions_1.updateSubscription)({
                id,
                error: {
                    error: error.error,
                    reason: error.reason,
                    message: error.message,
                },
            }));
        });
        // queries
        this.queryManager.on('create', ({ id, request }) => dispatch((0, actions_1.createQuery)({ id, request })));
        this.queryManager.on('delete', ({ id }) => dispatch((0, actions_1.deleteQuery)({ id })));
        this.queryManager.on('ready', ({ id, value }) => {
            this.flushUpdates();
            dispatch((0, actions_1.updateQuery)({ id, value, ready: true }));
        });
        this.queryManager.on('error', ({ id, error }) => {
            this.flushUpdates();
            dispatch((0, actions_1.updateQuery)({
                id,
                error: {
                    error: error.error,
                    reason: error.reason,
                    message: error.message,
                },
            }));
        });
        // entities
        this.on('flush', (entities) => dispatch((0, actions_1.replaceEntities)(entities)));
        // currentUser
        this.ddp.on('loggingIn', () => dispatch((0, actions_1.setLoggingIn)()));
        this.ddp.on('loginError', () => dispatch((0, actions_1.setLoggingIn)(false)));
        this.ddp.on('resumeLoginError', () => dispatch((0, actions_1.setLoggingIn)(false)));
        this.ddp.on('loggedIn', (userId) => dispatch((0, actions_1.setUser)({ _id: userId })));
        this.ddp.on('loggedOut', () => dispatch((0, actions_1.clearUser)()));
        this.ddp.on('restoring', () => dispatch((0, actions_1.setRestoring)(true)));
        this.ddp.on('restored', () => {
            this.flushUpdates();
            dispatch((0, actions_1.setRestoring)(false));
        });
    }
    scheduleUpdates(newCollections, delay = this.entitiesUpdateDelay) {
        if (this.schedule) {
            clearTimeout(this.schedule.handle);
        }
        const handle = setTimeout(() => {
            this.flushUpdates();
        }, delay);
        this.scheduled = {
            handle,
            newCollections,
        };
    }
    flushUpdates() {
        if (this.scheduled) {
            const { handle, newCollections } = this.scheduled;
            clearTimeout(handle);
            this.emit('flush', newCollections);
            delete this.scheduled;
        }
    }
    apply(name, params, options) {
        return this.ddp.apply(name, params, options);
    }
    refreshQuery(request) {
        return this.queryManager.refresh(request);
    }
    login(...args) {
        return this.ddp.login(...args);
    }
    logout(...args) {
        return this.ddp.logout(...args);
    }
    loginWithPassword({ username, email, password }) {
        const options = {
            password: this.constructor.hashPassword(password),
            user: { username, email },
        };
        return this.login(options);
    }
    createUser(_a) {
        var { password } = _a, rest = __rest(_a, ["password"]);
        const params = [
            Object.assign({ password: this.constructor.hashPassword(password) }, rest),
        ];
        return this.ddp.executeLoginRoutine('createUser', params);
    }
    resetPassword({ token, newPassword }) {
        const params = [
            {
                token,
                plainPassword: newPassword,
            },
        ];
        return this.ddp
            .executeLoginRoutine('resetPasswordWithValidation', params)
            .catch((error) => {
            var _a;
            // If the new method doesn't exist (backward compatibility)
            if (error.error === 404 ||
                ((_a = error.reason) === null || _a === void 0 ? void 0 : _a.includes("Method 'resetPasswordWithValidation' not found"))) {
                const oldParams = [token, this.constructor.hashPassword(newPassword)];
                return this.ddp.executeLoginRoutine('resetPassword', oldParams);
            }
            throw error;
        });
    }
}
DDPConnector.models = {};
DDPConnector.REQUEST_TYPE__SUBSCRIPTION = 'subscription';
DDPConnector.REQUEST_TYPE__QUERY = 'query';
exports.default = DDPConnector;
//# sourceMappingURL=DDPConnector.js.map